/**
 * La vignette d'un média — une photo, une vidéo (son affiche, son triangle, sa durée), un
 * vocal (son onde), un texte (ses premières lignes). Elle tient son ratio pendant le
 * chargement (rien ne saute), respire tant que l'image n'est pas là, dit « Indisponible »
 * quand aucune source ne charge — et rend alors la main par `onErreur`.
 *
 * Ce qu'elle porte sur l'image, en `Badge` posés dans un calque `.dark` (lisibles sur toute
 * photo) : le verrou payant (flouté, le cadenas et le prix au centre), payé (le prix au point
 * vert), le prix proposé, OFFERT, « Vu », la durée. Choisie, son cadre recule et sa
 * `Checkbox` se coche — jamais un contour coloré ; en multiple, un `Badge` à la marque dit son
 * rang. « Agrandir » est un `Button`. Aucune couleur ni pastille dessinée ici : quand Kayla
 * change le style des boutons, des pastilles ou ses teintes, la vignette suit.
 *
 * Trois façons de s'en servir :
 *  - à regarder : ni `onOuvrir` ni `selectionnable` → un simple cadre ;
 *  - à ouvrir : `onOuvrir` → toute la vignette est un bouton ;
 *  - à choisir : `selectionnable` + `onChoisir` → toute la vignette coche, et le petit
 *    bouton en coin (`onApercu`) agrandit sans cocher.
 *
 * Elle tient dans une grille (`taille="md"`) comme dans une bulle de conversation
 * (`taille="sm"`, `ratio="naturel"` : la forme de la photo, bornée par `--md-hauteur-max`).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode, type Ref } from "react";

import { cn } from "../cn";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { AlertTriangle, Eye, Gift, Lock, Maximize2, Mic, Play, StickyNote } from "../icons";
import { formatMinutage, listeDeSources, ratioCss } from "./format";
import { barresDecoratives } from "./onde";
import type { Media, Ratio, TypeDeMedia } from "./types";
import { useVisible } from "./useVisible";
import "./medias.css";

export type TailleVignette = "sm" | "md";

export interface LibellesVignette {
  indisponible: string;
  offert: string;
  regarde: string;
  paye: string;
  verrouille: string;
  agrandir: string;
  choisir: string;
  types: Record<TypeDeMedia, string>;
}

export const LIBELLES_VIGNETTE: LibellesVignette = {
  indisponible: "Indisponible",
  offert: "Offert",
  regarde: "Vu",
  paye: "Payé",
  verrouille: "Verrouillé",
  agrandir: "Agrandir",
  choisir: "Choisir",
  types: { image: "Photo", video: "Vidéo", audio: "Vocal", texte: "Texte" },
};

export interface VignetteProps
  extends Omit<Media, "id" | "plein" | "desactive" | "ratio">,
    Omit<HTMLAttributes<HTMLElement>, "title" | "onError" | "children"> {
  /** `md` (défaut) dans une grille ; `sm` dans une bulle, un album, une bande. */
  taille?: TailleVignette;
  /** Défaut 1 (carré). `naturel` : la forme du média, une fois chargé. */
  ratio?: Ratio;
  /** `couvrir` (défaut) rogne pour remplir ; `contenir` montre tout, en bandes. */
  ajustement?: "couvrir" | "contenir";
  /** Le nom, en bas, sur un dégradé (`true` = le `titre`). */
  legende?: ReactNode | true;
  /** Toute la vignette est un bouton qui ouvre le média. */
  onOuvrir?: () => void;
  /** La vignette se coche : sa coche en coin, toute sa surface pour cocher. */
  selectionnable?: boolean;
  choisie?: boolean;
  /** Le numéro d'ordre dans la sélection (l'ordre d'un album) — à la place de la coche. */
  rang?: number;
  onChoisir?: () => void;
  /** Le bouton « agrandir » en coin, quand la vignette se coche. */
  onApercu?: () => void;
  /** Ne se choisit pas : estompée, inerte. */
  desactivee?: boolean;
  /** Forcer un état : la vignette respire (`chargement`) ou dit « Indisponible » (`erreur`). */
  etat?: "chargement" | "erreur";
  /** Ne charge l'image qu'en approchant de l'écran (défaut `true`). */
  paresseuse?: boolean;
  /** Aucune source n'a chargé. L'app peut re-signer et rendre un nouveau `src`. */
  onErreur?: () => void;
  /** L'image est là ; son ratio naturel (largeur / hauteur). */
  onCharge?: (ratio: number) => void;
  /** En haut à droite : une action de l'app (retirer, un menu). */
  coin?: ReactNode;
  libelles?: Partial<LibellesVignette>;
  className?: string;
  style?: CSSProperties;
}

export function Vignette({
  type,
  src,
  titre,
  texte,
  duree,
  prix,
  verrouille = false,
  paye = false,
  offert = false,
  regarde = false,
  teinte,
  taille = "md",
  ratio = 1,
  ajustement = "couvrir",
  legende,
  onOuvrir,
  selectionnable = false,
  choisie = false,
  rang,
  onChoisir,
  onApercu,
  desactivee = false,
  etat,
  paresseuse = true,
  onErreur,
  onCharge,
  coin,
  libelles: libellesPartiels,
  className,
  style,
  ...reste
}: VignetteProps) {
  const libelles = { ...LIBELLES_VIGNETTE, ...libellesPartiels, types: { ...LIBELLES_VIGNETTE.types, ...libellesPartiels?.types } };
  const racine = useRef<HTMLElement | null>(null);
  const visible = useVisible(racine, paresseuse);

  const sources = useMemo(() => listeDeSources(src), [src]);
  const cleSources = sources.join("\n");
  const [essai, setEssai] = useState(0);
  const [chargee, setChargee] = useState(false);
  const [echec, setEchec] = useState(false);
  const [ratioNaturel, setRatioNaturel] = useState<number | null>(null);
  useEffect(() => {
    setEssai(0);
    setChargee(false);
    setEchec(false);
  }, [cleSources]);

  const montreImage = (type === "image" || type === "video") && sources.length > 0;
  const source = montreImage && visible && !echec ? sources[essai] : undefined;
  const enErreur = etat === "erreur" || (echec && type === "image");
  const enChargement = etat === "chargement" || (montreImage && !chargee && !enErreur && !echec);

  function surErreur() {
    if (essai < sources.length - 1) {
      setEssai(essai + 1);
      return;
    }
    setEchec(true);
    onErreur?.();
  }

  const naturel = ratio === "naturel";
  const aspect = naturel ? (ratioNaturel != null ? String(ratioNaturel) : "4 / 3") : ratioCss(ratio) ?? "1";
  const legendeAffichee = legende === true ? titre : legende;
  const dureeTexte = type === "video" || type === "audio" ? formatMinutage(duree) : "";
  const barres = useMemo(() => (type === "audio" ? barresDecoratives(titre ?? cleSources ?? "vocal", 32) : []), [type, titre, cleSources]);

  const nomAccessible = [
    titre || libelles.types[type],
    dureeTexte,
    verrouille ? `${libelles.verrouille}${prix ? ` · ${prix}` : ""}` : paye ? `${libelles.paye}${prix ? ` · ${prix}` : ""}` : prix,
    offert ? libelles.offert : "",
    regarde ? libelles.regarde : "",
  ]
    .filter(Boolean)
    .join(" · ");

  // Les pastilles sont des `Badge` (Kayla change leur style : elles suivent) ; la taille suit la vignette.
  const tailleBadge = taille === "sm" ? "sm" : "md";
  const prixHaut =
    !verrouille && paye ? (
      <Badge ton="vert" point size={tailleBadge}>
        {prix ?? libelles.paye}
      </Badge>
    ) : !verrouille && prix ? (
      <Badge ton="gris" size={tailleBadge} icon={<Lock aria-hidden />}>
        {prix}
      </Badge>
    ) : null;
  const enHaut = offert || prixHaut != null || coin != null;
  const enBas = regarde || dureeTexte !== "";

  const contenu = (
    <>
      <span className="md-vignette-cadre" aria-hidden>
        {type === "audio" ? (
          <span className="md-vignette-audio">
            <span className="md-barre-onde">
              {barres.map((h, i) => (
                <span key={i} style={{ height: `${Math.round(h * 100)}%` }} />
              ))}
            </span>
          </span>
        ) : type === "texte" ? (
          <span className="md-vignette-texte" style={teinte ? ({ "--md-teinte": teinte } as CSSProperties) : undefined}>
            <StickyNote aria-hidden />
            <p>{texte ?? titre ?? ""}</p>
          </span>
        ) : null}
        {source ? (
          <img
            key={source}
            className="md-vignette-media"
            src={source}
            alt=""
            loading={paresseuse ? "lazy" : undefined}
            decoding="async"
            draggable={false}
            data-chargee={chargee || undefined}
            data-flou={verrouille || undefined}
            data-contenir={ajustement === "contenir" || undefined}
            onLoad={(e) => {
              const img = e.currentTarget;
              setChargee(true);
              if (img.naturalWidth && img.naturalHeight) {
                const r = img.naturalWidth / img.naturalHeight;
                setRatioNaturel(r);
                onCharge?.(r);
              }
            }}
            onError={surErreur}
          />
        ) : null}
        {enChargement ? <span className="md-squelette" /> : null}
        {enErreur ? (
          <span className="md-etat">
            <AlertTriangle aria-hidden />
            {libelles.indisponible}
          </span>
        ) : null}
        {/* Ce qui se pose sur l'image : les jetons de la nuit (`dark`), quel que soit le thème. */}
        <span className="md-calque dark" data-haut={enHaut || selectionnable || undefined} data-bas={enBas || undefined}>
          {type === "video" && !verrouille && !enErreur ? <Play weight="fill" className="md-lecture" /> : null}
          {verrouille ? (
            <span className="md-verrou">
              <Badge ton="gris" size="md" icon={<Lock weight="fill" aria-hidden />}>
                {prix ?? libelles.verrouille}
              </Badge>
            </span>
          ) : null}
          {legendeAffichee ? <span className="md-legende">{legendeAffichee}</span> : null}
        </span>
      </span>

      {enHaut || enBas || selectionnable ? (
        <span className="md-calque dark">
          {enHaut ? (
            <span className="md-coins" data-cote="haut">
              {offert ? (
                <Badge ton="gris" size={tailleBadge} icon={<Gift aria-hidden />}>
                  {libelles.offert}
                </Badge>
              ) : null}
              {prixHaut}
              <span className="md-espace" />
              {coin ? <span className="md-coin-app">{coin}</span> : null}
            </span>
          ) : null}
          {enBas ? (
            <span className="md-coins" data-cote="bas" aria-hidden>
              {regarde ? (
                <Badge ton="gris" size={tailleBadge} icon={<Eye aria-hidden />}>
                  {libelles.regarde}
                </Badge>
              ) : null}
              <span className="md-espace" />
              {dureeTexte ? (
                <Badge ton="gris" size={tailleBadge} icon={type === "audio" ? <Mic aria-hidden /> : undefined}>
                  {dureeTexte}
                </Badge>
              ) : null}
            </span>
          ) : null}
          {selectionnable ? (
            // La surface qui coche porte le nom et l'état (`aria-pressed`) : la case n'est qu'un dessin, inerte.
            <span className="md-coche" aria-hidden inert>
              <Checkbox checked={choisie} readOnly size={taille === "sm" ? "sm" : "md"} />
              {choisie && rang != null ? (
                <Badge ton="marque" size={tailleBadge}>
                  {rang}
                </Badge>
              ) : null}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  const attributs = {
    ...reste,
    className: cn("md md-vignette", taille === "sm" ? "md-arrondi-sm" : "md-arrondi", className),
    style: { aspectRatio: aspect, ...style },
    "data-taille": taille,
    "data-type": type,
    "data-choisie": (selectionnable && choisie) || undefined,
    "data-coche": selectionnable || undefined,
    "data-desactive": desactivee || undefined,
    "data-legende": legendeAffichee ? true : undefined,
    "data-ratio-naturel": naturel || undefined,
  };
  const nom = reste["aria-label"] ?? nomAccessible;

  if (selectionnable) {
    return (
      <div {...attributs} ref={racine as Ref<HTMLDivElement>}>
        {contenu}
        <button
          type="button"
          className="md-vignette-surface"
          aria-pressed={choisie}
          aria-label={`${libelles.choisir} : ${nom}`}
          disabled={desactivee}
          onClick={onChoisir}
        />
        {onApercu ? (
          <span className="md-agrandir dark">
            {/* 32 px, le cran `sm` : à 48 il mangeait le coin du média (un chatteur, 03-11-2026 : « le bouton
                plein écran est un peu trop gros »). */}
            <Button
              variant="secondary"
              size="sm"
              iconOnly
              icon={<Maximize2 />}
              aria-label={`${libelles.agrandir} : ${nom}`}
              onClick={(e) => {
                e.stopPropagation();
                onApercu();
              }}
            />
          </span>
        ) : null}
      </div>
    );
  }
  if (onOuvrir) {
    return (
      <button
        {...attributs}
        ref={racine as Ref<HTMLButtonElement>}
        type="button"
        aria-label={nom}
        disabled={desactivee}
        onClick={onOuvrir}
      >
        {contenu}
      </button>
    );
  }
  return (
    <div {...attributs} ref={racine as Ref<HTMLDivElement>} role="img" aria-label={nom}>
      {contenu}
    </div>
  );
}
